import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { StatusType } from '@datadog/browser-logs';
import { isJupiterLite } from 'constants/Global.constants';
import { PUBLIC_PATHS } from 'constants/PublicPaths.constants';
import {
  setAuthStatus,
  setGetAuthToken,
  setIsFirstLogin,
  setLogin,
  setLogout,
  setShowUnauthorised,
  setUser,
} from 'ducks/auth/authSlice';
import { PROJECT_ROUTE_PATTERN } from 'routes/routes.constants';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';

import api from 'api';
import { HTTPError } from 'api/error';
import { LOGGER_TYPE } from 'api/types';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { logToDataDog } from 'lib/ddlogHelper';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import { getOTAuth0Params } from 'screens/OTLogin/utils';

export const Auth = ({ children }: PropsWithChildren) => {
  const { accountId, projectId } = useProjectRoute();
  const dispatch = useLocalDispatch();
  const toast = useToast();
  const authStatus = useAppSelector(state => state.auth.authStatus);
  const [shouldVerifyEmail, setShouldVerifyEmail] = useState(false);
  const pathname = usePathname();
  const hasRedirected = useRef(false);

  const isPublic = PUBLIC_PATHS.some(
    p => pathname === p || pathname.startsWith(p),
  );

  const {
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    user,
  } = useAuth0();

  const login = useCallback(
    async (path: string) => {
      if (!isAuthenticated) {
        if (hasRedirected.current) {
          return;
        }
        hasRedirected.current = true;

        // Add ext-brand=opentable to Auth0 params if user is an OT user for a
        // separate Auth0 log in screen.
        const otUserAuthParams = getOTAuth0Params();

        try {
          await loginWithRedirect({
            appState: {
              returnTo: path,
            },
            authorizationParams: otUserAuthParams,
          });
        } catch (e) {
          console.error('Error occurred while redirecting to login', e);
          hasRedirected.current = false;
        }
      }
    },
    [isAuthenticated, loginWithRedirect],
  );

  const authoriseUser = useCallback(async () => {
    try {
      dispatch(setAuthStatus('pending'));

      const result = await api.authoriseUser();

      // Handle undefined response from 401 redirect
      if (!result) {
        return;
      }

      const { isAuthorised, user } = result;

      dispatch(setAuthStatus(isAuthorised ? 'authorized' : 'unauthorized'));
      dispatch(setShowUnauthorised(!isAuthorised));
      if (user && user.is_first_login) {
        dispatch(setIsFirstLogin(true));
      }
    } catch (error: unknown) {
      logToDataDog(
        LOGGER_TYPE.USER_MANAGEMENT,
        StatusType.error,
        'Error whilst authorising user',
        {
          error,
        },
        accountId,
        projectId,
      );

      // Only show unauthorized for actual auth failures (403)
      // Don't show for 401 (handled by redirect) or network errors (will retry)
      if (error instanceof HTTPError && error?.httpStatus === 403) {
        dispatch(setAuthStatus('unauthorized'));
        dispatch(setShowUnauthorised(true));
      } else {
        // For other errors, set not authorised but don't show the unauthorized page
        // This allows the app to retry on next load
        dispatch(setAuthStatus('not-checked'));
      }
    }
  }, [accountId, dispatch, projectId]);

  const handleLogout = useCallback(async () => {
    if (hasRedirected.current) {
      return;
    }
    hasRedirected.current = true;
    dispatch(setShowUnauthorised(false));
    try {
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } catch {
      toast.error({
        title: 'We could not log you out. Try again!',
      });
      hasRedirected.current = false;
    }
  }, [dispatch, logout, toast]);

  // Effect 1: Store auth methods in Redux
  useEffect(() => {
    dispatch(setLogin(login));
    dispatch(
      setLogout(() => {
        void handleLogout();
      }),
    );
    dispatch(
      setGetAuthToken(async (force?: boolean) => {
        try {
          return await getAccessTokenSilently(
            force ? { cacheMode: 'off' } : undefined,
          );
        } catch (_e) {
          void handleLogout();
        }
      }),
    );
  }, [dispatch, login, handleLogout, getAccessTokenSilently]);

  // Effect 2: Handle authentication (login redirect)
  useEffect(() => {
    if (isPublic || isLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      login(window.location.pathname).catch(error => {
        toast.error({
          title: 'We could not log you in. Try again',
        });
        console.error('Error occurred while logging in', error);
      });
    }
  }, [isAuthenticated, user, isLoading, isPublic, login, toast]);

  // Effect 3: Handle email verification
  useEffect(() => {
    if (isPublic || isLoading || !isAuthenticated || !user) {
      return;
    }

    if (user && !user?.email_verified && isJupiterLite) {
      setShouldVerifyEmail(true);
    } else {
      setShouldVerifyEmail(false);
    }
  }, [isAuthenticated, user, isLoading, isPublic]);

  // Effect 4: Handle user authorisation
  useEffect(() => {
    if (isPublic || isLoading || !isAuthenticated || !user) {
      return;
    }

    // Don't authorize if email not verified
    if (user && !user?.email_verified && isJupiterLite) {
      return;
    }

    if (authStatus !== 'authorized' && authStatus !== 'pending') {
      void authoriseUser();
    }
  }, [isAuthenticated, authStatus, user, isLoading, isPublic, authoriseUser]);

  useEffect(() => {
    if (user) {
      dispatch(setUser(user));
    }
  }, [dispatch, user]);

  const isProjectRoute = PROJECT_ROUTE_PATTERN.test(pathname);

  if (!isPublic) {
    if (shouldVerifyEmail) {
      return <ErrorPage type={ErrorStatus.EMAIL_UNVERIFIED} />;
    }

    // For project routes, skip showing the Loader here - ProjectLoadingOverlay handles it
    if (!isAuthenticated || !user) {
      if (!isProjectRoute) {
        return <Loader inProjectPage={false} fullScreen={true} logo={true} />;
      }
    }
  }

  return children;
};
